import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { orderService } from '../services/firestore';
import { Order } from '../types';
import './CheckoutPage.css';

declare global {
  interface Window {
    Razorpay: any;
  }
}

const CheckoutPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { items, total, clearCart } = useCart();
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    pincode: ''
  });
  const [paymentMethod, setPaymentMethod] = useState<'cod' | 'online'>('cod');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [razorpayLoaded, setRazorpayLoaded] = useState(false);

  useEffect(() => {
    // Load Razorpay script
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => setRazorpayLoaded(true);
    document.body.appendChild(script);

    // Pre-fill user data if available
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: user.displayName || '',
        email: user.email || ''
      }));
    }

    return () => {
      document.body.removeChild(script);
    };
  }, [user]);

  useEffect(() => {
    if (items.length === 0) {
      navigate('/cart');
    }
  }, [items, navigate]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.mobile.trim()) {
      newErrors.mobile = 'Mobile number is required';
    } else if (!/^[0-9]{10}$/.test(formData.mobile.replace(/\s/g, ''))) {
      newErrors.mobile = 'Please enter a valid 10-digit mobile number';
    }

    if (!formData.addressLine1.trim()) {
      newErrors.addressLine1 = 'Address line 1 is required';
    }

    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }

    if (!formData.state.trim()) {
      newErrors.state = 'State is required';
    }

    if (!formData.pincode.trim()) {
      newErrors.pincode = 'Pincode is required';
    } else if (!/^[0-9]{6}$/.test(formData.pincode)) {
      newErrors.pincode = 'Please enter a valid 6-digit pincode';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const createRazorpayOrder = async (amount: number) => {
    // In a real application, you would create an order on your backend
    // For demo purposes, we'll use a mock order ID
    return {
      id: `order_${Date.now()}`,
      amount: amount * 100, // Razorpay expects amount in paise
      currency: 'INR'
    };
  };

  const handleRazorpayPayment = async () => {
    if (!razorpayLoaded) {
      setErrors({ submit: 'Payment gateway is loading. Please try again.' });
      return;
    }

    try {
      const razorpayOrder = await createRazorpayOrder(total);
      
      const options = {
        key: 'YOUR_RAZORPAY_KEY_ID', // Replace with your actual Razorpay key
        amount: razorpayOrder.amount,
        currency: razorpayOrder.currency,
        name: 'JK Prints',
        description: 'Purchase of custom printing products',
        order_id: razorpayOrder.id,
        handler: async (response: any) => {
          // Payment successful
          await handleOrderCreation('paid');
        },
        prefill: {
          name: formData.name,
          email: user?.email,
          contact: formData.mobile
        },
        theme: {
          color: '#667eea'
        },
        modal: {
          ondismiss: () => {
            setErrors({ submit: 'Payment was cancelled. Please try again.' });
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      console.error('Razorpay error:', error);
      setErrors({ submit: 'Failed to initiate payment. Please try again.' });
    }
  };

  const handleOrderCreation = async (paymentStatus: 'pending' | 'paid') => {
    try {
      const order: Omit<Order, 'id' | 'createdAt' | 'updatedAt'> = {
        customerName: formData.name,
        customerEmail: user?.email || '',
        customerPhone: formData.mobile,
        items: items,
        totalAmount: total,
        paymentMethod: paymentMethod,
        paymentStatus: paymentStatus,
        orderStatus: 'pending',
        shippingAddress: {
          addressLine1: formData.addressLine1,
          addressLine2: formData.addressLine2,
          city: formData.city,
          state: formData.state,
          pincode: formData.pincode
        }
      };

      await orderService.create(order);
      clearCart();
      navigate('/order-success');
    } catch (error) {
      console.error('Order creation error:', error);
      setErrors({ submit: 'Failed to place order. Please try again.' });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (!user) {
      setErrors({ submit: 'Please login to place an order' });
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      if (paymentMethod === 'online') {
        await handleRazorpayPayment();
      } else {
        await handleOrderCreation('pending');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      setErrors({ submit: 'Failed to process checkout. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(price);
  };

  if (items.length === 0) {
    return null; // Will redirect to cart
  }

  return (
    <div className="checkout-page">
      <div className="container">
        <div className="checkout-header">
          <h1>Checkout</h1>
          <p>Complete your order details</p>
        </div>

        <div className="checkout-content">
          <div className="checkout-form">
            <form onSubmit={handleSubmit}>
              <div className="form-section">
                <h3>Contact Information</h3>
                <div className="form-group">
                  <label htmlFor="name" className="form-label">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className={`form-input ${errors.name ? 'error' : ''}`}
                    placeholder="Enter your full name"
                    disabled={loading}
                  />
                  {errors.name && <span className="error-message">{errors.name}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="mobile" className="form-label">Mobile Number *</label>
                  <input
                    type="tel"
                    id="mobile"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleInputChange}
                    className={`form-input ${errors.mobile ? 'error' : ''}`}
                    placeholder="Enter your 10-digit mobile number"
                    disabled={loading}
                  />
                  {errors.mobile && <span className="error-message">{errors.mobile}</span>}
                </div>
              </div>

              <div className="form-section">
                <h3>Shipping Address</h3>
                <div className="form-group">
                  <label htmlFor="addressLine1" className="form-label">Address Line 1 *</label>
                  <textarea
                    id="addressLine1"
                    name="addressLine1"
                    value={formData.addressLine1}
                    onChange={handleInputChange}
                    className={`form-input form-textarea ${errors.addressLine1 ? 'error' : ''}`}
                    placeholder="Street address, apartment number, etc."
                    rows={2}
                    disabled={loading}
                  />
                  {errors.addressLine1 && <span className="error-message">{errors.addressLine1}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="addressLine2" className="form-label">Address Line 2 (Optional)</label>
                  <input
                    type="text"
                    id="addressLine2"
                    name="addressLine2"
                    value={formData.addressLine2}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Landmark, nearby location, etc."
                    disabled={loading}
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="city" className="form-label">City *</label>
                    <input
                      type="text"
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      className={`form-input ${errors.city ? 'error' : ''}`}
                      placeholder="Enter your city"
                      disabled={loading}
                    />
                    {errors.city && <span className="error-message">{errors.city}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="state" className="form-label">State *</label>
                    <input
                      type="text"
                      id="state"
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      className={`form-input ${errors.state ? 'error' : ''}`}
                      placeholder="Enter your state"
                      disabled={loading}
                    />
                    {errors.state && <span className="error-message">{errors.state}</span>}
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="pincode" className="form-label">Pincode *</label>
                  <input
                    type="text"
                    id="pincode"
                    name="pincode"
                    value={formData.pincode}
                    onChange={handleInputChange}
                    className={`form-input ${errors.pincode ? 'error' : ''}`}
                    placeholder="Enter 6-digit pincode"
                    disabled={loading}
                  />
                  {errors.pincode && <span className="error-message">{errors.pincode}</span>}
                </div>
              </div>

              <div className="form-section">
                <h3>Payment Method</h3>
                <div className="payment-options">
                  <label className="payment-option">
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="cod"
                      checked={paymentMethod === 'cod'}
                      onChange={(e) => setPaymentMethod(e.target.value as 'cod' | 'online')}
                      disabled={loading}
                    />
                    <span className="radio-custom"></span>
                    <div className="payment-info">
                      <strong>Cash on Delivery</strong>
                      <p>Pay when you receive your order</p>
                    </div>
                  </label>

                  <label className="payment-option">
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="online"
                      checked={paymentMethod === 'online'}
                      onChange={(e) => setPaymentMethod(e.target.value as 'cod' | 'online')}
                      disabled={loading}
                    />
                    <span className="radio-custom"></span>
                    <div className="payment-info">
                      <strong>Online Payment</strong>
                      <p>Pay now using Razorpay (Credit/Debit Card, UPI, Net Banking)</p>
                    </div>
                  </label>
                </div>
              </div>

              {errors.submit && (
                <div className="error-message submit-error">{errors.submit}</div>
              )}

              <div className="form-actions">
                <button
                  type="submit"
                  className="btn btn-primary btn-large"
                  disabled={loading || !user}
                >
                  {loading ? (
                    <>
                      <div className="loading"></div>
                      Processing...
                    </>
                  ) : (
                    `Place Order - ${formatPrice(total)}`
                  )}
                </button>
                
                <Link to="/cart" className="btn btn-secondary btn-large">
                  Back to Cart
                </Link>
              </div>
            </form>
          </div>

          <div className="order-summary">
            <div className="summary-card">
              <h3>Order Summary</h3>
              
              <div className="order-items">
                {items.map((item) => (
                  <div key={item.product.id} className="summary-item">
                    <div className="item-info">
                      <span className="item-name">{item.product.name}</span>
                      <span className="item-quantity">x{item.quantity}</span>
                    </div>
                    <span className="item-price">{formatPrice(item.product.price * item.quantity)}</span>
                  </div>
                ))}
              </div>
              
              <div className="summary-divider"></div>
              
              <div className="summary-row">
                <span>Subtotal</span>
                <span>{formatPrice(total)}</span>
              </div>
              
              <div className="summary-row">
                <span>Delivery Charges</span>
                <span>Calculated at checkout</span>
              </div>
              
              <div className="summary-divider"></div>
              
              <div className="summary-row total">
                <span>Total</span>
                <span>{formatPrice(total)}</span>
              </div>

              <div className="delivery-note">
                <p>Delivery charges may apply. You can also pick up your order from our office. Final confirmation will be shared via your mobile number.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
